import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { PhoneInput } from "../components/ui/phone-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useAuth } from "../components/context/auth/AuthState";
import OtpVerification from "../components/auth/OtpVerification";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AlertCircle, CheckCircle2, Leaf } from "lucide-react";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { currentUser, resetPassword } = useAuth();

  const [status, setStatus] = useState({ msg: "", type: "success" });
  const [phno, setPhno] = useState({ phone: "", valid: false });
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChangePhno = (value) => {
    setPhno({
      phone: value,
      valid: value ? isValidPhoneNumber(value) : false,
    });
    setStatus({ msg: "", type: "success" });
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ msg: "", type: "success" });

    if (!phno.valid) {
      setStatus({ msg: "Invalid phone number", type: "error" });
      setLoading(false);
      return;
    }

    if (otp.length !== 6) {
      setStatus({ msg: "Enter the 6-digit OTP", type: "error" });
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setStatus({ msg: "Password must be at least 6 characters", type: "error" });
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setStatus({ msg: "Passwords do not match", type: "error" });
      setLoading(false);
      return;
    }

    try {
      await resetPassword(phno.phone, otp, newPassword);
      navigate("/", { replace: true });
    } catch (error) {
      setStatus({
        msg: error.message || "Failed to reset password",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (currentUser) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="w-full min-h-screen flex items-center justify-center bg-white px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center">
              <Leaf className="h-6 w-6 text-emerald-600" />
            </div>
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900">
            Reset password
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Verify your phone via WhatsApp OTP to set a new password
          </p>
        </div>

        <form className="space-y-6" onSubmit={handleResetPassword}>
          {status.msg && (
            <Alert
              variant={status.type === "error" ? "destructive" : "default"}
              className={`border ${status.type === "error" ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}`}
            >
              {status.type === "error" ? (
                <AlertCircle className="h-4 w-4 text-red-600" />
              ) : (
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              )}
              <AlertDescription
                className={
                  status.type === "error" ? "text-red-800" : "text-emerald-800"
                }
              >
                {status.msg}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <PhoneInput
                value={phno.phone}
                onChange={handleChangePhno}
                defaultCountry="IN"
                className="w-full"
              />
            </div>

            <OtpVerification
              phone={phno.phone}
              phoneValid={phno.valid}
              purpose="forgot_password"
              otp={otp}
              onOtpChange={setOtp}
              onError={(msg) => msg && setStatus({ msg, type: "error" })}
            />

            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            disabled={loading}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </Button>

          <div className="text-center text-sm">
            <Link
              to="/login"
              className="font-medium text-emerald-600 hover:text-emerald-500"
            >
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
